import _ from 'underscore';
import { useCallback, useContext, useMemo } from 'react';
import { InstrumentationContext } from '@bingads-webui-react/hoc-with-instrumentation';
import { useCustomerLevelVariables, usePostponeReadVariables } from '@bingads-webui-campaign-react/basic-context-utils';
import { DEFAULT_BUDGET_VALUE_FOR_CURRENCY } from '@bingads-webui-campaign/ads-globalization';
import { useCountryCurrenciesMappings } from '@bingads-webui/overreact-cc';
import { usePromisify } from '@bingads-webui-react/overreact';

import { usePreference, preferenceNames } from './use-preference';

const defaultBudgetValue = 25;

export function useGetDefaultBudgetValueForCountry() {
  const customerLevelVariables = useCustomerLevelVariables();
  const postponeReadConfig = usePostponeReadVariables();

  const [, { read: readCountryCurrenciesMappingsFn }]
    = useCountryCurrenciesMappings(customerLevelVariables, postponeReadConfig);
  const [readCountryCurrenciesMappingsAsync] = usePromisify([readCountryCurrenciesMappingsFn]);
  const readCountryCurrenciesMappings = useCallback(
    (args = {}, ...rest) => readCountryCurrenciesMappingsAsync(args, ...rest),
    [readCountryCurrenciesMappingsAsync]
  );

  const instrumentation = useContext(InstrumentationContext);
  const readCurrencyActivity = useMemo(() => {
    if (instrumentation && instrumentation.pmaxLiteSignup) {
      const scenario = instrumentation.pmaxLiteSignup.create();
      return scenario.currencyCountryMapping.create();
    }
    return { trace: _.noop, error: _.noop };
  }, [instrumentation]);

  const [{ value: countryPreference }] = usePreference(preferenceNames.SelectedCountry);

  const getDefaultBudgetValueForCountry = useCallback(
    (selectedCountry = countryPreference) => readCountryCurrenciesMappings()
      .then((countryCurrenciesMappings) => {
        if (_.isEmpty(countryCurrenciesMappings) || _.isEmpty(selectedCountry)) {
          return defaultBudgetValue;
        }

        const c = selectedCountry.toUpperCase();
        const countryCurrency = _.findWhere(countryCurrenciesMappings, { country: c });

        const allowedCurrencies = _.get(countryCurrency, 'currencies');
        const defaultCurrency = _.get(countryCurrency, 'defaultCurrency');

        const targetCurrency = _.contains(allowedCurrencies, defaultCurrency)
          ? defaultCurrency : _.first(allowedCurrencies);

        if (targetCurrency && DEFAULT_BUDGET_VALUE_FOR_CURRENCY[targetCurrency]) {
          return DEFAULT_BUDGET_VALUE_FOR_CURRENCY[targetCurrency];
        }

        return defaultBudgetValue;
      })
      .catch((error) => {
        readCurrencyActivity.trace(`Error in readCountryCurrenciesMappings: ${JSON.stringify(error)}`);
        return defaultBudgetValue;
      }),
    [countryPreference, readCountryCurrenciesMappings, readCurrencyActivity]
  );

  return { getDefaultBudgetValueForCountry };
}
